import { ErrorMessage, PageContainer, Spinner } from "@/components";
import InterviewCard from "@/components/interviews/InterviewCard";
import { useApplicationsQuery } from "@/hooks/api/application.queries";
import { useInterviewsQuery } from "@/hooks/api/interview.queries";
import { cn } from "@/lib/utils";
import type { InterviewType } from "@/services/interview.service";
import {
  CalendarCheck,
  CalendarDays,
  Briefcase,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type TabKey = "upcoming" | "all";

const TABS: { key: TabKey; label: string }[] = [
  { key: "upcoming", label: "Upcoming" },
  { key: "all", label: "All Interviews" },
];

export default function InterviewsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>("upcoming");

  const upcomingQuery = useInterviewsQuery({ upcoming: true });
  const allQuery = useInterviewsQuery();

  // One extra request, not one per card: the interview payload carries no parent
  // application status, so rounds left behind by a withdrawal are flagged here.
  const { data: applicationsData } = useApplicationsQuery({ page: 1, limit: 100 });

  const applicationStatusById = useMemo(() => {
    const map = new Map<string, string>();
    for (const application of applicationsData?.items ?? []) {
      map.set(application.id, application.status);
    }
    return map;
  }, [applicationsData]);

  const activeQuery = tab === "upcoming" ? upcomingQuery : allQuery;
  const interviews: InterviewType[] = activeQuery.data ?? [];

  const refetchAll = () => {
    upcomingQuery.refetch();
    allQuery.refetch();
  };

  if (activeQuery.isError) {
    return (
      <ErrorMessage
        message="Failed to load your interviews."
        onRetry={refetchAll}
      />
    );
  }

  // Both lists gate the render: the empty state needs the full count to tell
  // "nothing scheduled" apart from "everything already happened".
  const isLoading = activeQuery.isLoading || allQuery.isLoading;
  const upcomingCount = upcomingQuery.data?.length ?? 0;
  const totalCount = allQuery.data?.length ?? 0;

  return (
    <PageContainer className="min-h-screen">
      {/* Hero Header */}
      <div className="relative mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-700 to-purple-700 p-8 text-white shadow-xl">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 50%, white 1px, transparent 1px)",
            backgroundSize: "50px 50px",
          }}
        />
        <div className="relative">
          <div className="mb-3 flex items-center gap-2">
            <Sparkles size={18} className="text-yellow-300" />
            <span className="text-sm font-semibold uppercase tracking-widest text-indigo-200">
              My Activity
            </span>
          </div>
          <h1 className="mb-2 text-4xl font-extrabold tracking-tight">
            My Interviews
          </h1>
          <p className="max-w-xl text-base text-indigo-200">
            Your recruiters schedule these rounds. Times are shown in your local
            timezone.
          </p>
          <div className="mt-5 flex items-center gap-3 text-sm text-indigo-200">
            <span className="flex items-center gap-1.5">
              <CalendarCheck size={14} />{" "}
              {allQuery.isLoading ? "..." : upcomingCount} Upcoming
            </span>
            <span className="h-1 w-1 rounded-full bg-indigo-400" />
            <span className="flex items-center gap-1.5">
              <CalendarDays size={14} />{" "}
              {allQuery.isLoading ? "..." : totalCount} Total
            </span>
            <span className="h-1 w-1 rounded-full bg-indigo-400" />
            <button
              onClick={refetchAll}
              className="flex items-center gap-1.5 transition hover:text-white"
            >
              <RefreshCw
                size={13}
                className={cn("inline", isLoading && "animate-spin")}
              />{" "}
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex flex-wrap gap-2">
        {TABS.map((item) => (
          <button
            key={item.key}
            onClick={() => setTab(item.key)}
            className={cn(
              "rounded-full border px-4 py-1.5 text-sm font-semibold transition-all",
              tab === item.key
                ? "border-indigo-600 bg-indigo-600 text-white shadow-sm"
                : "border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:text-indigo-600"
            )}
          >
            {item.label}
            {item.key === "upcoming" && upcomingCount > 0 && (
              <span className="ml-2 rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-bold">
                {upcomingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center gap-4 py-32">
          <Spinner />
          <p className="animate-pulse text-sm text-gray-400">
            Loading your interviews...
          </p>
        </div>
      ) : interviews.length === 0 ? (
        <EmptyState
          // "No upcoming" and "none at all" are genuinely different situations —
          // the first means every round is already done or cancelled.
          variant={
            tab === "upcoming" && totalCount > 0 ? "no-upcoming" : "no-interviews"
          }
          onBrowse={() => navigate("/discover/internships")}
          onViewAll={() => setTab("all")}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {interviews.map((interview) => (
            <InterviewCard
              key={interview.id}
              interview={interview}
              applicationStatus={applicationStatusById.get(
                interview.application_id
              )}
            />
          ))}
        </div>
      )}
    </PageContainer>
  );
}

function EmptyState({
  variant,
  onBrowse,
  onViewAll,
}: {
  variant: "no-upcoming" | "no-interviews";
  onBrowse: () => void;
  onViewAll: () => void;
}) {
  const isNoUpcoming = variant === "no-upcoming";

  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white py-32 text-center">
      <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-indigo-50">
        <CalendarDays size={32} className="text-indigo-300" />
      </div>
      <h3 className="mb-2 text-xl font-bold text-gray-700">
        {isNoUpcoming ? "No upcoming interviews" : "No interviews"}
      </h3>
      <p className="mb-6 max-w-xs text-sm text-gray-400">
        {isNoUpcoming
          ? "All of your scheduled rounds are already done. Past interviews are still in the full list."
          : "You don't have any interviews yet. Recruiters schedule these once your application moves forward."}
      </p>
      {isNoUpcoming ? (
        <button
          onClick={onViewAll}
          className="flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
        >
          <CalendarDays size={16} /> View All Interviews
        </button>
      ) : (
        <button
          onClick={onBrowse}
          className="flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
        >
          <Briefcase size={16} /> Browse Internships
        </button>
      )}
    </div>
  );
}
